/** QuickHouse — Écran d'accueil : liste, création, export, import, partage de projets */
import { useEffect, useRef, useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';

/** URL du portail de la suite, déduite du sous-domaine courant : l'accueil à
 *  tuiles est servi sur `portail.<base>` (house.quick-apps.fr → portail.quick-apps.fr).
 *  Null en local ou sur IP (pas de portail joignable par nom). */
function portalUrl() {
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || /^\d+(\.\d+)+$/.test(host)) return null;
  const labels = host.split('.');
  if (labels.length < 3) return null; // besoin d'un sous-domaine (app.base.tld)
  const base = labels.slice(1).join('.'); // quick-apps.fr
  return `${window.location.protocol}//portail.${base}/`;
}

export default function ProjectPicker() {
  const me = useProjectStore((s) => s.me);
  const loadMe = useProjectStore((s) => s.loadMe);
  const projects = useProjectStore((s) => s.projects);
  const loadProjects = useProjectStore((s) => s.loadProjects);
  const openProject = useProjectStore((s) => s.openProject);
  const createProject = useProjectStore((s) => s.createProject);
  const deleteProjectById = useProjectStore((s) => s.deleteProjectById);
  const shareProject = useProjectStore((s) => s.shareProject);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    loadMe();
    loadProjects().catch((e) => setError(e.message));
  }, [loadMe, loadProjects]);

  const create = async () => {
    if (!name.trim()) return;
    try { await createProject(name.trim()); } catch (e) { setError(e.message); }
  };

  // Partage : saisie de la liste des comptes (séparés par virgule/espace/point-virgule)
  const share = async (p) => {
    setError('');
    const current = (p.sharedWith || []).join(', ');
    const input = window.prompt(
      `Partager « ${p.name} » avec (identifiants séparés par des virgules) :`,
      current,
    );
    if (input === null) return; // annulé
    const list = input.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    try { await shareProject(p.id, list); } catch (e) { setError(e.message); }
  };

  // Export : télécharge le bundle (projet + textures + objets perso) en .quickhouse.json
  const exportProject = async (p) => {
    setError('');
    try {
      const res = await fetch(`/api/projects/${p.id}/export`);
      if (!res.ok) throw new Error('Export impossible');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${p.name.replace(/[^\w.-]+/g, '_') || 'projet'}.quickhouse.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setError(e.message); }
  };

  // Import : fichier .quickhouse.json (bundle) ou projet JSON nu → NOUVEAU projet
  const importFile = async (file) => {
    setError('');
    setBusy(`Import de ${file.name}…`);
    try {
      const text = await file.text();
      let json;
      try { json = JSON.parse(text); } catch { throw new Error('Fichier illisible : JSON attendu (.quickhouse.json)'); }
      const res = await fetch('/api/projects/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(json),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.details ? `${data.error} — ${data.details.slice(0, 3).join(' ; ')}` : (data.error ?? 'Import impossible'));
      }
      await loadProjects();
      setBusy('');
      if (window.confirm(`Projet « ${data.name} » importé. L'ouvrir maintenant ?`)) {
        await openProject(data.id);
      }
    } catch (e) {
      setBusy('');
      setError(e.message);
    }
  };

  const portal = portalUrl();

  return (
    <div className="picker">
      {/* Barre suite : identité + retour au portail Quick-Apps */}
      {me && (me.suite || portal) && (
        <div className="suite-bar">
          {portal && <a className="suite-back" href={portal} title="Retour au portail Quick-Apps">← Quick-Apps</a>}
          <span className="grow" />
          <span className="suite-user">
            {me.username}{me.admin ? ' (admin)' : ''}
          </span>
        </div>
      )}

      <h1>Quick<span className="logo-sfx">·House</span></h1>
      <div className="sub">Plans de maison 2D / 3D — famille QuickApps</div>
      <div className="new-row">
        <input
          placeholder="Nom du nouveau projet…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && create()}
        />
        <button onClick={create}>Créer</button>
        <button
          title="Importer un projet exporté (.quickhouse.json) — textures et objets personnalisés compris"
          onClick={() => fileRef.current?.click()}
        >
          📥 Importer…
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) importFile(f);
          }}
        />
      </div>
      <div className="list">
        {projects.map((p) => {
          const shared = (p.sharedWith || []).length;
          return (
            <div key={p.id} className="item" onClick={() => openProject(p.id).catch((e) => setError(e.message))}>
              <span className="grow">
                {p.name}
                {!p.mine && p.owner && <span className="badge" title={`Partagé par ${p.owner}`}>partagé</span>}
                {p.mine && shared > 0 && <span className="badge" title={p.sharedWith.join(', ')}>partagé ×{shared}</span>}
              </span>
              <span className="meta">
                {p.floors} étage{p.floors > 1 ? 's' : ''} — {new Date(p.updatedAt).toLocaleDateString('fr-FR')}
              </span>
              {p.mine && (
                <button
                  title="Partager ce projet avec d'autres comptes"
                  onClick={(e) => { e.stopPropagation(); share(p); }}
                >
                  👤
                </button>
              )}
              <button
                title="Exporter ce projet (bundle .quickhouse.json avec textures et objets personnalisés)"
                onClick={(e) => { e.stopPropagation(); exportProject(p); }}
              >
                ⤓
              </button>
              {p.mine && (
                <button
                  className="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Supprimer le projet « ${p.name} » ?`)) {
                      deleteProjectById(p.id).catch((err) => setError(err.message));
                    }
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          );
        })}
        {!projects.length && <div className="sub">Aucun projet — créez-en un pour commencer ou importez un .quickhouse.json.</div>}
      </div>
      {busy && <div className="sub">{busy}</div>}
      {error && <div style={{ color: 'var(--danger)' }}>{error}</div>}
    </div>
  );
}
